// public/js/pages/reports.js
// The Reports page — the data-export add-on's builder + run/download surface
// (docs/data-export.md). Gated by canExport(); the server re-checks the
// entitlement on every call.
import { canExport } from '../modules/rbac.js';
import { escapeHtml } from '../utils/escape.js';
import {
  listReports, createReport, deleteReport, runReport, waitForRun, downloadRun, loadMyTenants,
} from '../features/reports/api.js';
import { scopeTenantParam, inScope } from '../modules/tenant-scope.js';

const PERIOD_LABELS = {
  last7: 'Last 7 days', last30: 'Last 30 days', last90: 'Last 90 days',
  lastCalMonth: 'Last calendar month', lastCalQuarter: 'Last calendar quarter',
  ytd: 'Year to date', custom: 'Custom range',
};
const FORMAT_LABELS = { csv: 'CSV', xlsx: 'Excel', pdf: 'PDF' };

let CATALOGUE = null;
let TENANTS = [];

export async function initReportsPage(container) {
  if (!canExport()) {
    container.innerHTML = `<div class="alert alert-warning">The data-export add-on is not enabled for your account.</div>`;
    return;
  }

  container.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="h4 mb-0"><i class="fa-solid fa-file-arrow-down me-2 text-primary"></i>Reports</h2>
      <button id="btn-new-report" class="btn btn-success"><i class="fa-solid fa-plus me-2"></i>New report</button>
    </div>
    <div class="card shadow-sm">
      <div class="card-body p-0 table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light">
            <tr><th>Name</th><th>Subject</th><th>Period</th><th>Profile</th><th>Formats</th><th class="text-end pe-3">Run</th></tr>
          </thead>
          <tbody id="reports-tbody"><tr><td colspan="6" class="text-center text-muted py-4">Loading…</td></tr></tbody>
        </table>
      </div>
    </div>
    ${builderModalHTML()}
  `;

  try {
    const [{ tenants }, listed] = await Promise.all([loadMyTenants(), listReports()]);
    TENANTS = tenants || [];
    CATALOGUE = listed.catalogue;
    // Global tenant scope: under a single scope only that tenant's reports show.
    renderRows((listed.definitions || []).filter(inScope));
  } catch (e) {
    document.getElementById('reports-tbody').innerHTML =
      `<tr><td colspan="6" class="text-center text-danger py-4">Failed to load reports: ${escapeHtml(e.message)}</td></tr>`;
    return;
  }

  document.getElementById('btn-new-report').addEventListener('click', openBuilder);
}

function renderRows(defs) {
  const tbody = document.getElementById('reports-tbody');
  if (!defs.length) {
    tbody.innerHTML = `<tr><td colspan="6" class="text-center text-muted py-4">No reports yet. Click “New report” to build one.</td></tr>`;
    return;
  }
  const subjLabel = (k) => CATALOGUE?.subjects.find(s => s.key === k)?.label || k;
  tbody.innerHTML = defs.map(d => `
    <tr data-id="${d.reportId}">
      <td class="fw-semibold">${escapeHtml(d.name || 'Untitled')}</td>
      <td>${escapeHtml(subjLabel(d.subject))}</td>
      <td><span class="text-muted small">${escapeHtml(PERIOD_LABELS[d.period?.kind] || '—')}</span></td>
      <td><span class="badge badge-soft-gray">${d.profile === 'customer' ? 'Customer' : 'Internal'}</span></td>
      <td>${(d.formats || []).map(f => `<span class="badge badge-soft-blue me-1">${FORMAT_LABELS[f] || f}</span>`).join('')}</td>
      <td class="text-end pe-3">
        <div class="btn-group btn-group-sm">
          ${(d.formats || ['csv']).map(f => `<button class="btn btn-outline-primary btn-run" data-fmt="${f}">${FORMAT_LABELS[f] || f}</button>`).join('')}
          <button class="btn btn-outline-danger btn-del" title="Delete"><i class="fa-solid fa-trash"></i></button>
        </div>
      </td>
    </tr>`).join('');

  tbody.querySelectorAll('tr').forEach(tr => {
    const id = tr.dataset.id;
    tr.querySelectorAll('.btn-run').forEach(btn =>
      btn.addEventListener('click', () => runAndDownload(id, btn.dataset.fmt, btn)));
    tr.querySelector('.btn-del').addEventListener('click', async () => {
      if (!confirm('Delete this report definition?')) return;
      try { await deleteReport(id); tr.remove(); window.showToast('Report deleted', 'success'); }
      catch (e) { window.showToast(`Delete failed: ${e.message}`, 'danger'); }
    });
  });
}

async function runAndDownload(id, format, btn) {
  const group = btn.closest('.btn-group');
  group.querySelectorAll('button').forEach(b => b.disabled = true);
  const original = btn.innerHTML;
  btn.innerHTML = `<span class="spinner-border spinner-border-sm"></span>`;
  try {
    const { runId } = await runReport(id, format);
    const run = await waitForRun(runId);
    await downloadRun(runId, run.filename);
    if (run.truncated) window.showToast('Report ready — note: some rows were omitted (size cap).', 'warning');
    else window.showToast(`Report ready (${run.rows} rows)`, 'success');
  } catch (e) {
    window.showToast(`Report failed: ${e.message}`, 'danger');
  } finally {
    btn.innerHTML = original;
    group.querySelectorAll('button').forEach(b => b.disabled = false);
  }
}

// ── Builder modal ────────────────────────────────────────────────────────────
function builderModalHTML() {
  return `
    <div class="modal fade" id="reportBuilder" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="fa-solid fa-wand-magic-sparkles me-2"></i>New report</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3" id="rb-tenant-wrap">
              <label class="form-label">Tenant</label>
              <select id="rb-tenant" class="form-select tenant-blur"></select>
            </div>
            <div class="mb-3">
              <label class="form-label">Name</label>
              <input id="rb-name" type="text" class="form-control" placeholder="e.g. Monthly utilisation">
            </div>
            <div class="mb-3">
              <label class="form-label">Subject</label>
              <select id="rb-subject" class="form-select"></select>
            </div>
            <div class="mb-3" id="rb-period-wrap">
              <label class="form-label">Period</label>
              <select id="rb-period" class="form-select"></select>
            </div>
            <div class="row g-2 mb-3 d-none" id="rb-custom-wrap">
              <div class="col"><label class="form-label small">From</label><input id="rb-from" type="date" class="form-control"></div>
              <div class="col"><label class="form-label small">To</label><input id="rb-to" type="date" class="form-control"></div>
            </div>
            <div class="mb-3">
              <label class="form-label">Presentation</label>
              <select id="rb-profile" class="form-select">
                <option value="internal">Internal (dense, all columns)</option>
                <option value="customer">Customer-facing (cover page, curated)</option>
              </select>
            </div>
            <div class="mb-1">
              <label class="form-label">Formats</label>
              <div id="rb-formats" class="d-flex gap-3"></div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" id="rb-save" class="btn btn-success"><i class="fa-solid fa-check me-1"></i>Create</button>
          </div>
        </div>
      </div>
    </div>`;
}

function openBuilder() {
  const el = document.getElementById('reportBuilder');

  // Tenant picker: hidden when the user has exactly one tenant, and PREFILLED
  // + hidden under a single global tenant scope (docs/tenant-scope.md).
  const tenantWrap = document.getElementById('rb-tenant-wrap');
  const tenantSel = document.getElementById('rb-tenant');
  tenantSel.innerHTML = TENANTS.map(t => `<option value="${t.tenantId}">${escapeHtml(t.name || t.tenantId)}</option>`).join('');
  const scopeTid = scopeTenantParam();
  const scopePinned = scopeTid && scopeTid !== 'unassigned' && TENANTS.some(t => t.tenantId === scopeTid);
  if (scopePinned) tenantSel.value = scopeTid;
  tenantWrap.classList.toggle('d-none', TENANTS.length <= 1 || scopePinned);

  const subjectSel = document.getElementById('rb-subject');
  subjectSel.innerHTML = CATALOGUE.subjects.map(s => `<option value="${s.key}">${escapeHtml(s.label)}</option>`).join('');

  const periodSel = document.getElementById('rb-period');
  periodSel.innerHTML = CATALOGUE.periods.map(p => `<option value="${p}">${escapeHtml(PERIOD_LABELS[p] || p)}</option>`).join('');
  periodSel.value = 'last30';

  const formatsWrap = document.getElementById('rb-formats');
  formatsWrap.innerHTML = CATALOGUE.formats.map((f, i) => `
    <div class="form-check">
      <input class="form-check-input" type="checkbox" value="${f}" id="rb-fmt-${f}" ${i === 0 ? 'checked' : ''}>
      <label class="form-check-label" for="rb-fmt-${f}">${FORMAT_LABELS[f] || f}</label>
    </div>`).join('');

  const periodWrap = document.getElementById('rb-period-wrap');
  const customWrap = document.getElementById('rb-custom-wrap');
  const syncSubject = () => {
    const subj = CATALOGUE.subjects.find(s => s.key === subjectSel.value);
    periodWrap.classList.toggle('d-none', !subj?.usesPeriod);
  };
  const syncPeriod = () => customWrap.classList.toggle('d-none', periodSel.value !== 'custom');
  subjectSel.onchange = () => { syncSubject(); };
  periodSel.onchange = syncPeriod;
  syncSubject(); syncPeriod();

  // Reset simple fields.
  document.getElementById('rb-name').value = '';
  document.getElementById('rb-profile').value = 'internal';

  const modal = bootstrap.Modal.getOrCreateInstance(el);
  modal.show();

  const saveBtn = document.getElementById('rb-save');
  const fresh = saveBtn.cloneNode(true);
  saveBtn.replaceWith(fresh);
  fresh.addEventListener('click', async () => {
    const subject = subjectSel.value;
    const usesPeriod = CATALOGUE.subjects.find(s => s.key === subject)?.usesPeriod;
    const kind = periodSel.value;
    const formats = [...formatsWrap.querySelectorAll('input:checked')].map(i => i.value);
    if (!formats.length) { window.showToast('Pick at least one format', 'warning'); return; }

    const period = usesPeriod
      ? (kind === 'custom'
          ? { kind, from: document.getElementById('rb-from').value || null, to: document.getElementById('rb-to').value || null }
          : { kind })
      : { kind: 'last30' };

    const def = {
      tenantId: TENANTS.length <= 1 ? (TENANTS[0]?.tenantId) : tenantSel.value,
      name: document.getElementById('rb-name').value.trim() || 'Untitled report',
      subject,
      period,
      profile: document.getElementById('rb-profile').value,
      formats,
    };

    fresh.disabled = true;
    fresh.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span>Creating…`;
    try {
      await createReport(def);
      modal.hide();
      window.showToast('Report created', 'success');
      // Re-list scoped to the created tenant so a platform user sees it too.
      const listed = await listReports(def.tenantId);
      renderRows((listed.definitions || []).filter(inScope));
    } catch (e) {
      window.showToast(`Create failed: ${e.message}`, 'danger');
    } finally {
      fresh.disabled = false;
      fresh.innerHTML = `<i class="fa-solid fa-check me-1"></i>Create`;
    }
  });
}
